/**
 * NotificationContext
 *
 * Global context for notification state management.
 * Provides notifications data, counts, actions, and UI state.
 * Also includes system notifications from toast messages.
 */

import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useNotifications, useNotificationSubscription } from '@/hooks/notifications';
import { useToast } from '@/hooks/ui/use-toast';
import type {
  Notification,
  NotificationFilterType,
  NotificationContextValue,
  SystemNotification,
} from '@/types/notifications';

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export function useNotificationContext() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotificationContext must be used within NotificationProvider');
  }
  return context;
}

interface NotificationProviderProps {
  children: ReactNode;
}

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<NotificationFilterType>('all');

  // Get notifications data and actions
  const {
    notifications,
    isLoading,
    error,
    unreadCount,
    countByCategory,
    countByPriority,
    markAsRead,
    markAllAsRead,
    dismiss,
    dismissAll,
    refetch,
  } = useNotifications();

  // Get toast messages for system notifications
  const { toasts, clearHistory, removeToast } = useToast();

  // Convert toasts to system notifications (exclude info type)
  const systemNotifications: SystemNotification[] = useMemo(() => {
    return toasts
      .filter(t => t.type !== 'info') // Exclude info toasts
      .map(t => ({
        id: t.id,
        title: typeof t.title === 'string' ? t.title : String(t.title || ''),
        description: typeof t.description === 'string' ? t.description : undefined,
        type: t.type || 'default',
        timestamp: t.timestamp,
        category: t.category,
        businessName: t.businessName,
      }));
  }, [toasts]);

  const systemNotificationCount = systemNotifications.length;

  // Subscribe to real-time updates
  useNotificationSubscription({
    showToastOnNew: true,
    toastPriorities: ['urgent', 'action_required'],
  });

  // Filter notifications based on current filter
  const filteredNotifications = useMemo(() => {
    if (filter === 'all') return notifications;
    if (filter === 'system') return []; // System shows toast notifications, not DB notifications
    return notifications.filter(n => n.priority === filter);
  }, [notifications, filter]);

  // Reset filter when panel closes
  const handleSetIsOpen = useCallback((open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Delay filter reset to avoid UI flash during close animation
      setTimeout(() => setFilter('all'), 300);
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<NotificationContextValue>(() => ({
    notifications: filteredNotifications,
    isLoading,
    error,
    unreadCount,
    countByCategory,
    countByPriority,
    markAsRead,
    markAllAsRead,
    dismiss,
    dismissAll,
    refetch,
    isOpen,
    setIsOpen: handleSetIsOpen,
    filter,
    setFilter,
    // System notifications
    systemNotifications,
    systemNotificationCount,
    clearSystemNotifications: clearHistory,
    dismissSystemNotification: removeToast,
  }), [
    filteredNotifications,
    isLoading,
    error,
    unreadCount,
    countByCategory,
    countByPriority,
    markAsRead,
    markAllAsRead,
    dismiss,
    dismissAll,
    refetch,
    isOpen,
    handleSetIsOpen,
    filter,
    systemNotifications,
    systemNotificationCount,
    clearHistory,
    removeToast,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export { NotificationContext };
